// Layout A "Workbench" (approved 2026-10-10): title bar, activity bar,
// explorer (package, inputs, messages), editor with tabs (the review grid
// first), problems / samples / revisions panel, AI assistant on the right,
// status bar. Spans the whole window; panels collapse on narrow screens.
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { Icon, Logo, type IconName } from "./icons";
import { classesOf, groups, groupOf, hintsOf, shownState, storedSteps, type Actions, type Step, type WorkbenchData } from "./model";
import { SetupPanel, EmptyReview } from "./Setup";
import { KeyDetail } from "./KeyDetail";
import { FileView } from "./FileView";
import { PublishView } from "./Publish";
import { ExamplesView } from "./Examples";
import { DesignView } from "./Design";
import type { Finding, ItemState, ReviewItem, Revision, SampleResult } from "../lib/types";
import { setTheme, theme } from "../lib/theme";
import { t, fmtDateTime, fmtNumber } from "../i18n";
import { LangSwitch } from "../pages/Login";

type Filter = "all" | "review" | "low" | "questions";

export function Workbench({ data, actions }: { data: WorkbenchData; actions: Actions }) {
  const gs = useMemo(() => groups(data.items), [data.items]);
  const [picked, setGroup] = useState(() => {
    const it = data.items.find((i) => i.kind === "key" && i.key_path === new URLSearchParams(location.search).get("key"));
    return it ? groupOf(it) : "";
  });
  const group = gs.some((g) => g.name === picked) ? picked : (gs[0]?.name ?? "");
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // deep links: ?key=<key path> opens a key's detail, ?file=<path> a file tab
  const params = new URLSearchParams(location.search);
  const [focus, setFocus] = useState<string | null>(() => data.items.find((i) => i.kind === "key" && i.key_path === params.get("key"))?.id ?? null);
  const [explorer, setExplorer] = useState(true);
  const [openFiles, setOpenFiles] = useState<string[]>(() => (params.get("file") ? [params.get("file")!] : []));
  const isB = data.project.scenario === "B";
  // scenario B opens on the Design tab until there is something to review
  const [active, setActive] = useState<string>(() => params.get("file") ?? (["publish", "examples", "design"].includes(params.get("view") ?? "") ? params.get("view")!
    : isB && !data.setup && !data.items.some((i) => i.kind === "key") ? "design" : "review")); // "review", "design", "publish", "examples" or a file path
  // scenario B: the set-up just finished -> the Design tab
  const hadSetup = useRef(!!data.setup);
  useEffect(() => {
    if (hadSetup.current && !data.setup && isB) setActive("design");
    hadSetup.current = !!data.setup;
  }, [data.setup]);
  const openFile = (path: string) => {
    setOpenFiles((f) => (f.includes(path) ? f : [...f, path]));
    setActive(path);
  };
  const closeFile = (path: string) => {
    setOpenFiles((f) => f.filter((x) => x !== path));
    if (active === path) setActive("review");
  };
  const [assistant, setAssistant] = useState(true);
  const [, setDark] = useState(theme());

  const [query, setQuery] = useState("");
  const keys = data.items.filter((i) => i.kind === "key");
  const done = keys.filter((k) => k.state !== "proposed").length;
  const q = query.trim().toLowerCase();
  // a search looks at every message: key, field, label, role
  const inGroup = q
    ? keys.filter((k) => {
        const h = hintsOf(k);
        return [k.key_path, k.proposal.field, k.proposal.message, h.label as string, h.role as string].some((x) => x?.toLowerCase().includes(q));
      })
    : keys.filter((k) => groupOf(k) === group);
  const rows = inGroup.filter((k) => {
    const st = shownState(k, data.items);
    if (filter === "review") return k.state === "proposed";
    if (filter === "low") return k.confidence === "low";
    if (filter === "questions") return st === "question";
    return true;
  });
  const count = (f: Filter) =>
    inGroup.filter((k) =>
      f === "review" ? k.state === "proposed" : f === "low" ? k.confidence === "low" : shownState(k, data.items) === "question",
    ).length;
  const highReady = inGroup.filter((k) => k.state === "proposed" && k.confidence === "high" && shownState(k, data.items) !== "question");
  const writer = data.me.role === "builder" || data.me.role === "admin";
  const decided = keys.filter((k) => k.state === "accepted" || k.state === "edited").length;
  // accepted keys whose AI proposal changed since (shown as a diff): take them all at once.
  // Keys the person edited are left out: taking the new proposal would drop their edit
  // (BoatAngel's rent_state label) - those are taken one by one in the key detail.
  const diffs = keys.filter((k) => !!k.pending_proposal && k.state !== "edited");
  const editedDiffs = keys.filter((k) => !!k.pending_proposal && k.state === "edited").length;
  // the decisions last written in this session: the button says so until one changes
  const decisionSig = keys.filter((k) => k.state === "accepted" || k.state === "edited").map((k) => `${k.id}:${k.state}:${JSON.stringify(k.edited ?? null)}`).join("|");
  const [writtenSig, setWrittenSig] = useState("");
  const focusItem = focus ? keys.find((k) => k.id === focus) : undefined;
  const [busy, setBusy] = useState("");
  const run = async (what: string, fn: () => Promise<void>) => {
    setBusy(what);
    try {
      await fn();
    } finally {
      setBusy("");
    }
  };

  const toggle = (id: string) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const bulk = async (action: "accept" | "reject") => {
    for (const id of selected) await actions.decide(id, action);
    setSelected(new Set());
  };

  return (
    <div class="flex h-full min-h-0 flex-col bg-bar text-ink">
      <TitleBar data={data} keysDone={done} keysTotal={keys.length} onAssistant={() => setAssistant(!assistant)} onPublish={() => setActive("publish")}
        query={query} onQuery={(v) => { setQuery(v); setActive("review"); }}
        onTheme={() => { const th = theme() === "dark" ? "light" : "dark"; setTheme(th); setDark(th); }} />
      <div class="flex min-h-0 flex-1">
        <ActivityBar explorer={explorer} onExplorer={() => setExplorer(!explorer)} onPublish={() => setActive("publish")}
          onExamples={() => setActive("examples")} onReview={() => setActive("review")} active={active}
          onDesign={isB && !data.setup ? () => setActive("design") : undefined}
          onValidate={writer && data.project.head_rev > 0 ? () => run("validate", actions.validate) : undefined} />
        {explorer && <Explorer data={data} actions={actions} writer={writer} groups={gs} group={group} done={done} total={keys.length}
          active={active} onOpen={openFile}
          onGroup={(g) => { setGroup(g); setSelected(new Set()); setFocus(null); setActive("review"); }} />}
        <main class="flex min-w-0 flex-1 flex-col bg-main">
          <Tabs group={group ? groupLabel(group) : t("wb.exp.package")} files={active === "publish" ? [...openFiles, "publish"] : openFiles} active={active}
            onSelect={setActive} onClose={closeFile} examples={!data.setup} design={isB && !data.setup} />
          {active === "design" && isB && !data.setup ? (
            <DesignView data={data} actions={actions} writer={writer} onOpen={openFile} />
          ) : active === "publish" ? (
            <PublishView data={data} actions={actions} writer={writer} />
          ) : active === "examples" ? (
            <ExamplesView data={data} actions={actions} writer={writer} />
          ) : active !== "review" ? (
            <FileView path={active} rev={data.project.head_rev} actions={actions} writer={writer} />
          ) : data.setup ? (
            <SetupPanel data={data} actions={actions} writer={writer} />
          ) : keys.length === 0 ? (
            <EmptyReview data={data} actions={actions} writer={writer} onDesign={() => setActive("design")} />
          ) : (
            <>
              <div class="flex min-h-[52px] flex-wrap items-center gap-2.5 border-b border-line px-4 py-2">
                <div class="flex gap-1 rounded-lg border border-line2 bg-side p-[3px]" role="tablist" aria-label={t("wb.filter.aria")}>
                  {([["all", t("wb.filter.all", { count: inGroup.length })], ["review", t("wb.filter.review", { count: count("review") })], ["low", t("wb.filter.low", { count: count("low") })],
                    ["questions", t("wb.filter.questions", { count: count("questions") })]] as [Filter, string][]).map(([f, label]) => (
                    <button type="button" role="tab" aria-selected={filter === f} onClick={() => setFilter(f)}
                      class={`pill h-6 px-2.5 ${filter === f ? "bg-sel text-ink" : "text-muted hover:text-ink"}`}>{label}</button>
                  ))}
                </div>
                <div class="flex-1" />
                {selected.size > 0 && <span class="text-[12.5px] text-muted">{t("wb.selected", { count: selected.size })}</span>}
                {writer && selected.size > 0 && <button type="button" class="btn" onClick={() => bulk("reject")}>{t("wb.reject")}</button>}
                {writer && selected.size > 0 && <button type="button" class="btn" onClick={() => bulk("accept")}>{t("wb.accept.n", { count: selected.size })}</button>}
                {writer && diffs.length > 0 && (
                  <button type="button" class="btn" disabled={!!busy}
                    title={t("wb.adopt.title") + (editedDiffs ? " " + t("wb.adopt.title.edited", { count: editedDiffs }) : "")}
                    onClick={() => run("adopt", async () => {
                      for (const k of diffs) await actions.decide(k.id, "adopt");
                    })}>
                    {busy === "adopt" ? t("wb.adopt.busy") : t("wb.adopt", { count: diffs.length })}
                  </button>
                )}
                {writer && (
                  <button type="button" class="btn border-accent/60 text-accent2" disabled={highReady.length === 0}
                    onClick={() => actions.acceptHigh(highReady.map((k) => k.key_path))}>
                    <Icon name="check" size={14} stroke={2.2} />{t("wb.accepthigh", { count: highReady.length })}
                  </button>
                )}
                {writer && (
                  writtenSig !== "" && writtenSig === decisionSig ? (
                    <span class="inline-flex items-center gap-1.5 text-[13px] text-good" role="status"><Icon name="check" size={14} stroke={2.4} />{t("wb.written")}</span>
                  ) : (
                    <button type="button" class="btn btn-pri" disabled={decided === 0 || !!busy}
                      title={data.project.scenario === "C" ? t("wb.write.title.c") : t("wb.write.title.pb")}
                      onClick={() => run("apply", async () => {
                        await actions.applyReview();
                        setWrittenSig(decisionSig);
                      })}>
                      {busy === "apply" ? t("wb.write.busy") : t("wb.write", { count: decided })}
                    </button>
                  )
                )}
              </div>
              <div class="flex min-h-0 flex-1">
                <ReviewGrid rows={rows} items={data.items} group={q ? t("wb.grid.search", { q: query.trim() }) : groupLabel(group)} selected={selected} focus={focus}
                  onToggle={toggle} onFocus={setFocus} writer={writer} />
                {focusItem && <KeyDetail item={focusItem} actions={actions} writer={writer} onClose={() => setFocus(null)} logicTypes={data.logicTypes} geoFamilies={data.geoTags ? data.geoFamilies : undefined} paramClass={data.paramClass} />}
              </div>
            </>
          )}
          <BottomPanel data={data} actions={actions} onExamples={() => setActive("examples")}
            onValidate={writer ? () => run("validate", actions.validate) : undefined} validating={busy === "validate"} />
        </main>
        {assistant && <Assistant data={data} actions={actions} writer={writer} onClose={() => setAssistant(false)} />}
      </div>
      <StatusBar data={data} />
    </div>
  );
}

function TitleBar({ data, keysDone, keysTotal, onAssistant, onTheme, onPublish, query, onQuery }: {
  data: WorkbenchData; keysDone: number; keysTotal: number; onAssistant: () => void; onTheme: () => void; onPublish: () => void;
  query: string; onQuery: (v: string) => void;
}) {
  const search = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        search.current?.focus();
      }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  const v = data.validation;
  return (
    <header class="flex h-12 flex-none items-center gap-4 border-b border-line bg-bar pl-2.5 pr-3">
      <a href="/" class="flex items-center gap-2.5 no-underline text-ink lg:w-[300px]">
        <Logo />
        <span class="hidden leading-tight sm:block"><span class="block text-[14px] font-semibold">gIoTHub</span>
          <span class="block text-[11px] text-dim">Type Builder</span></span>
      </a>
      <nav aria-label={t("wb.breadcrumb")} class="flex min-w-0 items-center gap-2 text-[13px] text-muted">
        <span class="hidden whitespace-nowrap md:inline">{data.me.org}</span><span class="hidden text-line2 md:inline">/</span>
        <span class="truncate font-semibold text-ink">{data.project.name}</span>
        <span class="chip hidden md:inline-flex">{data.project.type_id}</span>
        <span class="chip whitespace-nowrap bg-warn-soft text-warn">{t("wb.draft", { rev: data.project.head_rev })}</span>
      </nav>
      <div class="flex flex-1 justify-center">
        <label class="hidden h-8 w-full max-w-[520px] items-center gap-2.5 rounded-lg border border-line2 bg-side px-3 text-dim xl:flex">
          <Icon name="search" size={15} stroke={2} />
          <input ref={search} aria-label={t("wb.search.aria")} placeholder={t("wb.search.placeholder")} value={query}
            onInput={(e) => onQuery((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === "Escape") onQuery(""); }}
            class="flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-dim" />
          <kbd class="whitespace-nowrap rounded border border-line2 px-1.5 font-mono text-[11px]">{t("wb.search.kbd")}</kbd>
        </label>
      </div>
      <div class="flex items-center gap-2.5">
        <span class="hidden h-[30px] items-center gap-2 whitespace-nowrap rounded-[7px] border border-line2 bg-side px-2.5 text-[12.5px] text-muted md:inline-flex">
          <span class={`h-2 w-2 rounded-full ${!v ? "bg-dim" : v.ok ? "bg-good" : "bg-crit"}`} />
          {!v ? t("wb.val.none") : v.ok ? t(v.warnings === 1 ? "wb.val.ok.one" : "wb.val.ok.many", { count: v.warnings }) : t(v.errors === 1 ? "wb.val.err.one" : "wb.val.err.many", { count: v.errors })}
        </span>
        <span class="hidden whitespace-nowrap font-mono text-[12px] text-dim 2xl:inline">{t("wb.keys.count", { done: keysDone, total: keysTotal })}</span>
        <button type="button" class="btn btn-ghost w-[30px] justify-center px-0" aria-label={t("wb.theme")} onClick={onTheme}>
          <Icon name={theme() === "dark" ? "sun" : "moon"} size={15} />
        </button>
        <button type="button" class="btn btn-ghost w-[30px] justify-center px-0" aria-label={t("wb.assistant.toggle")} onClick={onAssistant}>
          <Icon name="panel" size={15} />
        </button>
        <button type="button" class="btn btn-pri" onClick={onPublish} disabled={data.project.head_rev === 0}
          title={data.summary.blocking > 0 ? t("wb.publish.blocked", { count: data.summary.blocking }) : t("wb.publish.title")}>
          {t("wb.publish")}<Icon name="chevron" size={12} stroke={2.4} />
        </button>
        <span class="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full bg-[#2E6680] text-[12px] font-semibold text-white"
          title={`${data.me.name} · ${data.me.role ? t(`role.${data.me.role}`) : ""}`}>{data.me.initials}</span>
        <span class="hidden text-muted lg:block"><LangSwitch /></span>
      </div>
    </header>
  );
}

function ActivityBar({ explorer, onExplorer, onPublish, onExamples, onReview, onValidate, onDesign, active }: {
  explorer: boolean; onExplorer: () => void; onPublish: () => void; onExamples: () => void; onReview: () => void; onValidate?: () => void;
  onDesign?: () => void; active: string;
}) {
  const b = (name: IconName, label: string, on = false, onClick?: () => void) => (
    <button type="button" aria-label={label} title={label} onClick={onClick}
      class={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${on ? "bg-hover text-ink" : "text-dim hover:text-ink"}`}>
      <Icon name={name} size={20} stroke={1.7} />
    </button>
  );
  return (
    <nav aria-label={t("wb.act.nav")} class="flex w-[52px] flex-none flex-col items-center gap-1.5 border-r border-line bg-bar py-2.5">
      {b("projects", t("nav.projects"), false, () => (location.href = "/"))}
      {b("file", t("wb.act.explorer"), explorer, onExplorer)}
      {onDesign && b("design", t("wb.act.design"), active === "design", onDesign)}
      {b("review", t("wb.act.review"), active === "review", onReview)}
      {onValidate && b("shield", t("wb.act.validate"), false, onValidate)}
      {b("flask", t("wb.act.examples"), active === "examples", onExamples)}
      {b("publish", t("wb.publish"), active === "publish", onPublish)}
      <div class="flex-1" />
      {b("help", t("wb.act.help"), false, () => window.open("/help", "_blank"))}
    </nav>
  );
}

function UploadButton({ label, accept, onFiles }: { label: string; accept?: string; onFiles: (f: File[]) => Promise<void> }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <button type="button" class="btn btn-ghost h-6 w-6 justify-center px-0" aria-label={label} title={label} disabled={busy}
        onClick={() => ref.current?.click()}>
        <Icon name={busy ? "spin" : "plus"} size={14} stroke={2.2} class={busy ? "animate-spin" : ""} />
      </button>
      <input ref={ref} type="file" multiple accept={accept} class="hidden" onChange={async (e) => {
        const el = e.target as HTMLInputElement;
        const files = [...(el.files ?? [])];
        el.value = "";
        if (!files.length) return;
        setBusy(true);
        try {
          await onFiles(files);
        } finally {
          setBusy(false);
        }
      }} />
    </>
  );
}

// middle shortens a name in the middle, so files that share a long prefix
// ("Topic Content BoatAngel_Commands.pdf", "..._Settings.pdf") stay apart.
function middle(name: string, max: number): string {
  if (name.length <= max) return name;
  const tail = Math.ceil((max - 1) * 0.6);
  return name.slice(0, max - 1 - tail) + "…" + name.slice(name.length - tail);
}

function Explorer({ data, actions, writer, groups: gs, group, done, total, onGroup, active, onOpen }: {
  data: WorkbenchData; actions: Actions; writer: boolean; groups: ReturnType<typeof groups>; group: string; done: number; total: number;
  onGroup: (g: string) => void; active: string; onOpen: (path: string) => void;
}) {
  const tree = useMemo(() => {
    const out: { name: string; depth: number; dir: boolean; path: string }[] = [];
    const seen = new Set<string>();
    for (const f of [...data.files].sort((a, b) => a.path.localeCompare(b.path))) {
      const parts = f.path.split("/");
      for (let i = 0; i < parts.length - 1; i++) {
        const d = parts.slice(0, i + 1).join("/");
        if (!seen.has(d)) { seen.add(d); out.push({ name: parts[i] + "/", depth: i, dir: true, path: d }); }
      }
      out.push({ name: parts[parts.length - 1], depth: parts.length - 1, dir: false, path: f.path });
    }
    return out;
  }, [data.files]);
  // folders fold; samples/ starts folded (one project has dozens of files there)
  const [folded, setFolded] = useState<Set<string>>(() => new Set(["samples"]));
  const toggle = (d: string) => setFolded((f) => {
    const n = new Set(f);
    if (n.has(d)) n.delete(d);
    else n.add(d);
    return n;
  });
  const hidden = (path: string) => [...folded].some((d) => path.startsWith(d + "/") && !(active === path));
  const count = (d: string) => data.files.filter((f) => f.path.startsWith(d + "/")).length;
  return (
    <aside aria-label={t("wb.act.explorer")} class="hidden w-[248px] flex-none flex-col gap-[18px] overflow-y-auto border-r border-line bg-side px-2 py-3.5 min-[1500px]:flex min-[2000px]:w-[272px]">
      <section>
        <h2 class="sec flex items-center justify-between pb-1 pl-3 pr-1"><span>{t("wb.exp.package")}</span>
          {writer && (data.project.scenario === "C"
            ? <UploadButton label={t("wb.exp.add.c")} accept=".js,.bin,.hex,.json,.md" onFiles={actions.uploadSources} />
            : <UploadButton label={t("wb.exp.add.pb")} accept=".proto,.bin,.hex,.json,.md" onFiles={actions.uploadSources} />)}</h2>
        {tree.length === 0 && <p class="px-3 text-[12.5px] text-dim">{
          data.project.scenario === "C" ? t("wb.exp.empty.c")
            : data.project.scenario === "B" ? t("wb.exp.empty.b")
            : t("wb.exp.empty.a")}</p>}
        {tree.filter((n) => !hidden(n.path)).map((n) =>
          n.dir ? (
            <button type="button" class="tree w-full text-left" style={{ paddingLeft: `${12 + n.depth * 18}px` }}
              aria-expanded={!folded.has(n.path)} onClick={() => toggle(n.path)} title={folded.has(n.path) ? t("wb.exp.showfiles") : t("wb.exp.fold")}>
              <Icon name="folder" size={14} stroke={2} class="text-accent" />
              <span class="flex-1 truncate font-mono text-[12.5px]">{n.name}</span>
              {folded.has(n.path) && <span class="text-[11px] text-dim">{count(n.path)}</span>}
            </button>
          ) : (
            <button type="button" onClick={() => onOpen(n.path)} aria-current={active === n.path}
              class={`tree w-full text-left ${active === n.path ? "tree-sel" : ""}`} style={{ paddingLeft: `${12 + n.depth * 18}px` }}>
              <Icon name="file" size={14} stroke={2} class={n.path.startsWith("proto/") ? "text-accent" : "text-muted"} />
              <span class="flex-1 truncate font-mono text-[12.5px]">{n.name}</span>
              {n.path === "ai_guide.md" && <span class="text-[11px] text-violet">{t("wb.exp.ai")}</span>}
            </button>
          ),
        )}
      </section>
      <section>
        <h2 class="sec flex items-center justify-between pb-1 pl-3 pr-1"><span>{t("wb.exp.inputs")}</span>
          {writer && <UploadButton label={t("wb.exp.add.inputs")} onFiles={actions.uploadInputs} />}</h2>
        {data.inputs.length === 0 && <p class="px-3 text-[12.5px] text-dim">{t("wb.exp.inputs.empty")}</p>}
        {data.inputs.map((f) => (
          <div class="tree" title={f.name}>
            <Icon name="file" size={14} stroke={2} class="text-violet" />
            <span class="flex-1 truncate">{middle(f.name, 20)}</span>
            <span class="text-[11px] text-dim">{size(f.size)}</span>
          </div>
        ))}
      </section>
      <section class="min-h-0 flex-1">
        <h2 class="sec flex justify-between px-3 pb-2"><span>{data.project.scenario === "C" ? t("wb.exp.groups") : t("wb.exp.messages")}</span>
          <span class="font-medium normal-case tracking-normal">{done} / {total}</span></h2>
        {gs.map((g) => {
          const pct = g.total ? Math.round((g.done / g.total) * 100) : 0;
          return (
            <button type="button" onClick={() => onGroup(g.name)} aria-current={g.name === group}
              class={`tree h-[30px] w-full text-left ${g.name === group ? "tree-sel" : ""}`}>
              <span class="flex-1 truncate">{groupLabel(g.name)}</span>
              {g.questions > 0 && <span class="pill h-4 bg-warn-soft px-1.5 text-warn">{g.questions}</span>}
              <span class="h-1 w-[54px] overflow-hidden rounded-sm bg-line2">
                <span class={`block h-1 ${pct === 100 ? "bg-good" : "bg-accent"}`} style={{ width: `${pct}%` }} />
              </span>
              <span class="w-[38px] text-right font-mono text-[11px] text-dim">{g.done}/{g.total}</span>
            </button>
          );
        })}
      </section>
    </aside>
  );
}

function Tabs({ group, files, active, onSelect, onClose, examples, design }: {
  group: string; files: string[]; active: string; onSelect: (t: string) => void; onClose: (p: string) => void; examples: boolean; design: boolean;
}) {
  const cls = (on: boolean) =>
    `flex h-[38px] flex-none items-center gap-2 border-r border-line px-3.5 text-[13px] ${on ? "bg-main text-ink shadow-[inset_0_2px_0_var(--color-accent)]" : "bg-side text-muted hover:text-ink"}`;
  return (
    <div role="tablist" class="flex h-[38px] flex-none overflow-x-auto border-b border-line bg-side">
      {design && (
        <button type="button" role="tab" aria-selected={active === "design"} class={cls(active === "design")} onClick={() => onSelect("design")}>
          <Icon name="design" size={13} stroke={2} class="text-accent" />{t("wb.tab.design")}
        </button>
      )}
      <button type="button" role="tab" aria-selected={active === "review"} class={cls(active === "review")} onClick={() => onSelect("review")}>
        <Icon name="check" size={13} stroke={2.2} class="text-accent" />{t("wb.tab.review", { group })}
      </button>
      {examples && (
        <button type="button" role="tab" aria-selected={active === "examples"} class={cls(active === "examples")} onClick={() => onSelect("examples")}>
          <Icon name="flask" size={13} stroke={2} class="text-accent" />{t("wb.tab.examples")}
        </button>
      )}
      {files.map((f) => (
        <div role="tab" aria-selected={active === f} class={`${cls(active === f)} pr-1.5`}>
          <button type="button" class={f === "publish" ? "text-[13px]" : "font-mono text-[12.5px]"} onClick={() => onSelect(f)}>{f === "publish" ? t("wb.publish") : f.split("/").pop()}</button>
          <button type="button" class="btn btn-ghost h-5 w-5 justify-center px-0" aria-label={t("wb.tab.close", { file: f === "publish" ? t("wb.publish") : f })} onClick={() => onClose(f)}><Icon name="x" size={11} stroke={2.2} /></button>
        </div>
      ))}
    </div>
  );
}

// display name of a review group: the built-in group names are translated,
// message names (data) stay as they are
function groupLabel(name: string): string {
  return name === "Commands" ? t("wb.group.commands") : name === "Keys" ? t("wb.group.keys") : name;
}

const stateStyle: Record<ItemState | "question", string> = {
  accepted: "bg-good-soft text-good",
  proposed: "bg-sel text-accent2",
  edited: "bg-violet-soft text-violet",
  question: "bg-warn-soft text-warn",
  rejected: "bg-crit-soft text-crit",
  open: "bg-warn-soft text-warn",
  answered: "bg-good-soft text-good",
  dismissed: "bg-hover text-dim",
};

const confBars: Record<string, string[]> = {
  high: ["bg-good", "bg-good", "bg-good"],
  medium: ["bg-warn", "bg-warn", "bg-line2"],
  low: ["bg-crit", "bg-line2", "bg-line2"],
};

const COLS = "grid grid-cols-[28px_minmax(170px,230px)_96px_124px_minmax(150px,236px)_minmax(150px,1fr)_88px_92px] min-[2200px]:grid-cols-[28px_minmax(200px,260px)_104px_140px_minmax(180px,260px)_minmax(180px,1fr)_minmax(0,1.3fr)_92px_96px] items-center gap-x-3 px-4";

function ReviewGrid({ rows, items, group, selected, focus, onToggle, onFocus, writer }: {
  rows: ReviewItem[]; items: ReviewItem[]; group: string; selected: Set<string>; focus: string | null;
  onToggle: (id: string) => void; onFocus: (id: string) => void; writer: boolean;
}) {
  return (
    <div class="min-h-0 flex-1 overflow-auto">
      <div class="min-w-[1080px]">
        <div class={`${COLS} sticky top-0 z-10 h-8 border-b border-line2 bg-side text-[11px] font-semibold uppercase tracking-[.06em] text-dim`}>
          <span /><span>{t("wb.col.key")}</span><span>{t("wb.col.class")}</span><span>{t("wb.col.kind")}</span><span>{t("wb.col.role")}</span><span>{t("wb.col.label")}</span><span class="hidden min-[2200px]:block">{t("wb.col.why")}</span><span>{t("wb.col.conf")}</span><span>{t("wb.col.state")}</span>
        </div>
        <div class="flex h-[34px] items-center border-b border-line bg-side/60 px-4 text-[12px] text-muted">
          <span class="font-mono text-ink2">{group}</span>&nbsp;· {t(rows.length === 1 ? "wb.grid.shown.one" : "wb.grid.shown.many", { count: rows.length })}
        </div>
        {rows.length === 0 && <p class="px-4 py-6 text-muted">{t("wb.grid.none")}</p>}
        {rows.map((r) => {
          const p = hintsOf(r);
          const st = shownState(r, items);
          const q = Object.entries(p.qualifiers ?? {}).map(([k, v]) => `${k}: ${v}`).join(", ");
          const cls = classesOf(p).map((c) => c.toLowerCase().replace("timeseries", "series")).join(", ");
          const bars = confBars[r.confidence ?? "low"];
          return (
            <div onClick={() => onFocus(r.id)} title={r.reason}
              class={`${COLS} h-9 cursor-default border-b border-line/70 text-[13px] hover:bg-hover ${focus === r.id ? "bg-sel/60 hover:bg-sel/60" : ""}`}>
              <span>
                {writer && (
                  <input type="checkbox" aria-label={t("wb.grid.select", { key: r.key_path })} checked={selected.has(r.id)}
                    onClick={(e) => e.stopPropagation()} onChange={() => onToggle(r.id)}
                    class="h-3.5 w-3.5 accent-[var(--color-accent)]" />
                )}
              </span>
              <span class="truncate font-mono text-[12.5px] text-ink">{r.key_path}</span>
              <span class="truncate text-ink2" title={cls}>{cls || "-"}</span>
              <span class="truncate text-ink2">{r.proposal.kind || "-"} <span class="font-mono text-[12px] text-dim">{p.unit}</span></span>
              <span class="flex items-center gap-1.5 overflow-hidden whitespace-nowrap">
                <span class="font-mono text-[12.5px] text-accent2">{p.role || "-"}</span>
                {q && <span class="chip">{q}</span>}
                {r.pending_proposal && <span class="pill h-4 bg-violet-soft px-1.5 text-violet" title={t("wb.grid.diff.title")}>diff</span>}
              </span>
              <span class="truncate text-ink" title={p.ai?.meaning ?? p.description}>{p.label}</span>
              <span class="hidden truncate text-[12.5px] text-muted min-[2200px]:block">{r.reason}</span>
              <span class="flex items-center gap-1.5 text-ink2">
                <span class="flex gap-0.5" aria-hidden="true">{bars.map((c) => <span class={`h-3 w-1 rounded-[1px] ${c}`} />)}</span>
                {r.confidence ? t(`wb.conf.${r.confidence}`) : ""}
              </span>
              <span><span class={`pill ${stateStyle[st]}`}>{t(`wb.state.${st}`)}</span></span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RevisionList({ data, actions }: { data: WorkbenchData; actions: Actions }) {
  const [rs, setRs] = useState<Revision[] | null>(null);
  useEffect(() => {
    actions.revisions().then(setRs).catch(() => setRs([]));
  }, [data.project.head_rev]);
  if (!rs) return <p class="px-4 py-2 text-muted">{t("common.loading")}</p>;
  if (rs.length === 0) return <p class="px-4 py-2 text-muted">{t("wb.rev.none")}</p>;
  return (
    <div>
      {rs.map((r) => (
        <div class="grid grid-cols-[48px_70px_minmax(0,1fr)_170px] items-center gap-2.5 px-4 py-1 text-[12.5px]">
          <span class="font-mono text-accent2">r{r.rev}</span>
          <span class={`pill h-4 px-1.5 ${r.author_kind === "ai" ? "bg-violet-soft text-violet" : "bg-hover text-ink2"}`}>{r.author_kind === "ai" ? t("wb.rev.ai") : t("wb.rev.person")}</span>
          <span class="truncate text-ink2">{r.message || "-"}</span>
          <span class="text-right text-[11.5px] text-dim">{fmtDateTime(r.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

// what a sample gives the geolocation service (geo tags switch, GIOTHUB-64)
function geoNote(r: SampleResult): string {
  if (!r.geo) return "";
  const parts = [r.geo.positions.length ? t("wb.geo.positions", { count: r.geo.positions.length }) : "", r.geo.evidence.wifi.length ? t("wb.geo.wifi", { count: r.geo.evidence.wifi.length }) : "",
    r.geo.evidence.cells.length ? t("wb.geo.cells", { count: r.geo.evidence.cells.length }) : "", r.geo.evidence.ble.length ? t("wb.geo.ble", { count: r.geo.evidence.ble.length }) : ""].filter(Boolean);
  return parts.length ? t("wb.geo.note", { list: parts.join(", ") }) : "";
}

function SamplesList({ data, actions, onExamples }: { data: WorkbenchData; actions: Actions; onExamples: () => void }) {
  const [rs, setRs] = useState<SampleResult[] | null>(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (data.project.head_rev === 0) return;
    actions.runExamples(false).then(setRs).catch((e) => setErr((e as Error).message));
  }, [data.project.head_rev]);
  if (data.project.head_rev === 0) return <p class="px-4 py-2 text-muted">{t("wb.smp.nopkg")}</p>;
  if (err) return <p class="px-4 py-2 text-muted">{err}</p>;
  if (!rs) return <p class="px-4 py-2 text-muted">{t("wb.smp.running")}</p>;
  return (
    <div>
      <div class="flex items-center gap-3 px-4 py-1.5 text-[12.5px] text-muted">
        <span>{t("wb.smp.summary", { ok: rs.filter((r) => r.ok).length, total: rs.length })}</span>
        <button type="button" class="btn h-6 px-2 text-[12px]" onClick={onExamples}>{t("wb.smp.open")}</button>
      </div>
      {rs.map((r) => (
        <div class="grid grid-cols-[18px_minmax(140px,260px)_minmax(0,1fr)] items-center gap-2.5 px-4 py-1 text-[12.5px]">
          <Icon name={r.ok ? "check" : "warn"} size={14} stroke={2.2} class={r.ok ? "text-good" : "text-warn"} />
          <span class="truncate font-mono text-[12px] text-accent2">{r.name}</span>
          <span class="truncate text-ink2">{r.ok ? t("wb.smp.ok", { ms: fmtNumber(r.duration_ms, 2), geo: geoNote(r) }) : r.error || (r.diff ?? [])[0] || (r.issues ?? [])[0] || t("wb.smp.differs")}</span>
        </div>
      ))}
    </div>
  );
}

function BottomPanel({ data, actions, onExamples, onValidate, validating }: {
  data: WorkbenchData; actions: Actions; onExamples: () => void; onValidate?: () => void; validating: boolean;
}) {
  const [tab, setTab] = useState<"problems" | "samples" | "revisions">("problems");
  const v = data.validation;
  const findings: Finding[] = v?.report?.findings ?? [];
  const problems = findings.filter((f) => f.level !== "info").length;
  const icon = (f: Finding) => (f.level === "error" ? "error" : f.level === "warning" ? "warn" : "info") as IconName;
  const color = (f: Finding) => (f.level === "error" ? "text-crit" : f.level === "warning" ? "text-warn" : "text-accent");
  const tabBtn = (id: typeof tab, label: ComponentChildren) => (
    <button type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
      class={`flex h-[34px] items-center gap-1.5 px-1 text-[12px] ${tab === id ? "text-ink shadow-[inset_0_-2px_0_var(--color-accent)]" : "text-muted hover:text-ink"}`}>{label}</button>
  );
  return (
    <section aria-label={t("wb.panel.aria")} class="flex h-[236px] flex-none flex-col border-t border-line2 bg-side min-[2000px]:h-[280px]">
      <div role="tablist" class="flex h-[34px] flex-none items-center gap-[18px] border-b border-line px-4">
        {tabBtn("problems", <>{t("wb.panel.problems")} {problems > 0 && <span class="pill h-4 bg-warn-soft text-warn">{problems}</span>}</>)}
        {tabBtn("samples", <>{t("wb.panel.samples")} {v && <span class={`font-mono text-[11px] ${v.samples_ok === v.samples_total ? "text-good" : "text-crit"}`}>{v.samples_ok}/{v.samples_total}</span>}</>)}
        {tabBtn("revisions", t("wb.panel.revisions"))}
        <div class="flex-1" />
        {v && <span class="hidden font-mono text-[11.5px] text-dim md:inline">{t("wb.panel.engine", { engine: v.engine, ms: v.duration_ms, rev: v.rev })}</span>}
        {onValidate && data.project.head_rev > 0 && (
          <button type="button" class="btn h-6 px-2 text-[12px]" onClick={onValidate} disabled={validating}>{validating ? t("wb.panel.checking") : t("wb.panel.validate")}</button>
        )}
      </div>
      <div class="min-h-0 flex-1 overflow-y-auto py-1.5">
        {tab === "problems" && !v && <p class="px-4 py-2 text-muted">{t("wb.panel.notvalidated")}</p>}
        {tab === "problems" && findings.map((f) => (
          <div class="grid grid-cols-[18px_minmax(140px,260px)_minmax(0,1fr)] items-center gap-2.5 px-4 py-1 text-[12.5px] min-[1500px]:grid-cols-[18px_minmax(160px,260px)_minmax(0,1fr)_120px]">
            <Icon name={icon(f)} size={14} stroke={2.2} class={color(f)} />
            <span class="truncate font-mono text-[12px] text-accent2">{f.path}</span>
            <span class="text-ink2">{f.message}</span>
            <span class="hidden text-right text-[11.5px] text-dim min-[1500px]:block">{f.code}</span>
          </div>
        ))}
        {tab === "samples" && <SamplesList data={data} actions={actions} onExamples={onExamples} />}
        {tab === "revisions" && <RevisionList data={data} actions={actions} />}
      </div>
    </section>
  );
}

function StepList({ steps }: { steps: Step[] }) {
  return (
    <div class="rounded-[10px] border border-line bg-bar px-3 py-2.5">
      {steps.map((s) => (
        <div class="flex min-h-[26px] items-center gap-2.5 text-[12.5px] text-ink2">
          <Icon name={s.state === "running" ? "spin" : s.state === "error" ? "warn" : "check"} size={14} stroke={2.4}
            class={s.state === "running" ? "animate-spin text-accent" : s.state === "error" ? "text-warn" : "text-good"} />
          <span class="w-[116px] flex-none font-mono text-[12px] text-accent2">{s.tool}</span>
          <span class="flex-1 truncate text-muted" title={s.text}>{s.text}</span>
        </div>
      ))}
    </div>
  );
}

/** Light formatting of the AI's text: paragraphs, `code`, **bold**. */
function Prose({ text }: { text: string }) {
  return (
    <div class="flex flex-col gap-2 text-[13.5px] leading-[1.55] text-ink">
      {text.split(/\n{2,}/).map((para) => (
        <p class="m-0 whitespace-pre-wrap">
          {para.split(/(`[^`]+`|\*\*[^*]+\*\*)/).map((part) =>
            part.startsWith("`") && part.endsWith("`") ? <code class="rounded bg-hover px-1 font-mono text-[12.5px] text-accent2">{part.slice(1, -1)}</code>
              : part.startsWith("**") && part.endsWith("**") ? <strong>{part.slice(2, -2)}</strong> : part)}
        </p>
      ))}
    </div>
  );
}

function Assistant({ data, actions, writer, onClose }: { data: WorkbenchData; actions: Actions; writer: boolean; onClose: () => void }) {
  const [text, setText] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [sendErr, setSendErr] = useState("");
  // start at the newest message and follow new text while the person is near the bottom
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  useEffect(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [data.turns.length, data.live.text, data.live.steps.length, data.live.running, data.items.length]);
  const questions = data.items.filter((i) => i.kind === "question" && i.state === "open");
  const steps = useMemo(() => storedSteps(data.turns), [data.turns]);
  const live = data.live;
  const canSend = writer && !data.aiPaused && !live.running;
  const send = async () => {
    if (!text.trim() || !canSend) return;
    const msg = text;
    setText("");
    setSendErr("");
    try {
      await actions.send(msg);
    } catch (e) {
      setText(msg);
      setSendErr((e as Error).message);
    }
  };
  const visible = data.turns.filter((tn) => tn.content.some((b) => b.type === "text" || b.type === "tool_use"));
  return (
    <aside aria-label={t("wb.ai.aria")} class="hidden w-[360px] min-[1500px]:w-[400px] flex-none flex-col border-l border-line bg-side lg:flex min-[2000px]:w-[448px]">
      <div class="flex h-12 flex-none items-center gap-2.5 border-b border-line px-4">
        <span class="inline-flex h-[22px] w-[22px] items-center justify-center rounded-md bg-sel text-accent2"><Icon name="spark" size={13} stroke={2} /></span>
        <span class="text-[14px] font-semibold">{t("wb.ai.title")}</span>
        <span class="text-[12px] text-dim">{t("wb.ai.scenario", { s: data.project.scenario, kind: data.project.scenario === "C" ? "JavaScript" : "protobuf" })}</span>
        <div class="flex-1" />
        <span class="inline-flex items-center gap-1.5 text-[11.5px] text-muted">
          <span class={`h-[7px] w-[7px] rounded-full ${data.aiPaused ? "bg-warn" : live.running ? "animate-pulse bg-accent" : "bg-good"}`} />
          {data.aiPaused ? t("wb.ai.paused") : live.running ? t("wb.ai.working") : t("wb.ai.ready")}
        </span>
        <button type="button" class="btn btn-ghost h-7 w-7 justify-center px-0" aria-label={t("wb.ai.hide")} onClick={onClose}><Icon name="x" size={14} /></button>
      </div>
      <div ref={scroller} onScroll={(e) => {
        const el = e.currentTarget as HTMLDivElement;
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      }} class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-[18px] py-[18px]">
        {data.aiPaused && (
          <div class="rounded-[10px] border border-warn/40 bg-warn-soft px-3.5 py-3 text-[13px] leading-normal text-ink">
            {t("wb.ai.pausedtext")}
          </div>
        )}
        {visible.length === 0 && !live.running && !data.aiPaused && (
          <p class="m-0 text-[13px] leading-normal text-muted">{
            data.files.length === 0
              ? t("wb.ai.empty.nofiles")
              : data.project.scenario === "C"
                ? t("wb.ai.empty.c")
                : data.project.scenario === "B"
                  ? t("wb.ai.empty.b")
                  : t("wb.ai.empty.a")}</p>
        )}
        {visible.map((tn) =>
          tn.role === "user" ? (
            <div class="max-w-[340px] self-end whitespace-pre-wrap rounded-[12px_12px_4px_12px] bg-sel px-3.5 py-2.5 text-[13.5px] leading-normal">
              {tn.content.filter((b) => b.type === "text").map((b) => b.text).join("\n")}
            </div>
          ) : (
            <div class="flex flex-col gap-2.5">
              {tn.content.some((b) => b.type === "text" && b.text) && <Prose text={tn.content.filter((b) => b.type === "text").map((b) => b.text).join("\n\n")} />}
              {steps.get(tn.id) && <StepList steps={steps.get(tn.id)!} />}
            </div>
          ),
        )}
        {live.running && (
          <div class="flex flex-col gap-2.5">
            {live.text ? <Prose text={live.text} /> : live.steps.length === 0 && <p class="m-0 text-[13px] text-muted">{t("wb.ai.thinking")}</p>}
            {live.steps.length > 0 && <StepList steps={live.steps} />}
          </div>
        )}
        {(live.error || sendErr) && (
          <div class="rounded-[10px] border border-crit/40 bg-crit-soft px-3.5 py-3 text-[13px] leading-normal">{live.error || sendErr}</div>
        )}
        {questions.map((q, i) => (
          <div class="rounded-[10px] border border-warn/40 bg-warn-soft/50 p-3.5">
            <div class="text-[11.5px] font-semibold uppercase tracking-[.04em] text-warn">
              {t(q.blocking ? "wb.ai.question.blocking" : "wb.ai.question", { n: i + 1, total: questions.length })}
            </div>
            {q.key_path && <div class="mt-2 font-mono text-[12.5px] text-accent2">{q.key_path}</div>}
            <p class="mb-0 mt-1 text-[13.5px] leading-normal">{q.question}</p>
            {writer && (
              <div class="mt-3 flex flex-col gap-2">
                <label class="sr-only" for={`ans-${q.id}`}>{t("wb.ai.answer.label")}</label>
                <textarea id={`ans-${q.id}`} rows={2} class="field h-auto py-1.5" placeholder={t("wb.ai.answer.label")}
                  value={answers[q.id] ?? ""} onInput={(e) => setAnswers({ ...answers, [q.id]: (e.target as HTMLTextAreaElement).value })} />
                <div class="flex flex-wrap gap-2">
                  <button type="button" class="btn btn-pri" disabled={!(answers[q.id] ?? "").trim()}
                    onClick={() => actions.decide(q.id, "answer", answers[q.id])}>{t("wb.ai.answer")}</button>
                  <button type="button" class="btn" onClick={() => actions.decide(q.id, "answer", "Yes")}>{t("common.yes")}</button>
                  <button type="button" class="btn" onClick={() => actions.decide(q.id, "answer", "No")}>{t("common.no")}</button>
                  <button type="button" class="btn btn-ghost" onClick={() => actions.decide(q.id, "dismiss")}>{t("wb.ai.dismiss")}</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div class="flex-none px-4 pb-4 pt-1">
        <div class="rounded-xl border border-line2 bg-main px-3 pb-2.5 pt-3">
          <label for="ask" class="sr-only">{t("wb.ai.ask")}</label>
          <textarea id="ask" rows={2} value={text} disabled={!writer || data.aiPaused}
            onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={questions.length ? t("wb.ai.ph.questions") : data.project.scenario === "C" ? t("wb.ai.ph.c") : t("wb.ai.ph.a")}
            class="w-full resize-none bg-transparent text-[13.5px] leading-normal text-ink outline-none placeholder:text-dim" />
          <div class="mt-1.5 flex items-center gap-2">
            <span class="flex-1 text-[11px] text-dim">{live.running ? t("wb.ai.busy") : t("wb.ai.hint")}</span>
            <button type="button" class="btn btn-pri w-[30px] justify-center px-0" aria-label={t("wb.ai.send")} onClick={send} disabled={!text.trim() || !canSend}>
              <Icon name="up" size={14} stroke={2.4} />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

function StatusBar({ data }: { data: WorkbenchData }) {
  return (
    <footer class="flex h-[26px] flex-none items-center gap-[18px] bg-status px-3.5 text-[11.5px] text-status-ink">
      <span class="font-mono">{t("wb.st.draft", { rev: data.project.head_rev })}</span>
      <span>{t("wb.st.engine", { engine: data.validation?.engine ?? "-" })}</span>
      <span class="hidden sm:inline">{data.project.scenario === "C" ? t("wb.st.quickjs") : t("wb.st.pb")}</span>
      {data.demo && <span class="rounded bg-white/15 px-1.5">{t("wb.st.demo")}</span>}
      <div class="flex-1" />
      <span>{t("wb.st.region", { region: data.me.region })}</span>
      {data.aiUsage !== "-" && <span class="hidden sm:inline">{t("wb.st.usage", { usage: data.aiUsage })}</span>}
    </footer>
  );
}

function size(b: number): string {
  if (b >= 1 << 20) return `${fmtNumber(b / (1 << 20), 1)} MB`;
  if (b >= 1024) return `${fmtNumber(b / 1024, 1)} kB`;
  return `${b} B`;
}
